import React, { useEffect, useState } from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

const MAX_MEDIA_HEIGHT = 60; // ~5 table rows

// helper: File -> dataURL
const fileToDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

const GeneratePDF = ({ formData, reportType = 'default', onComplete }) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [viewMode, setViewMode] = useState('');
  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    let isMounted = true;

    const generateAndUploadPDF = async () => {
      // 1) pre-resolve any File -> dataURL so drawing is fully synchronous
      const resolved = await Promise.all(
        (formData || []).map(async (step) => {
          let photo = step.photo || null;
          if (!photo && step.photoFile instanceof File) {
            try {
              photo = await fileToDataURL(step.photoFile);
            } catch {
              photo = null;
            }
          }
          return {
            ...step,
            photo,
          };
        })
      );

      const doc = new jsPDF();
      let currentY = 20;

      // header
      doc.setFontSize(20);
      const titleMap = {
        'fault-sim': 'Fault Sim Report',
        'asset-workflow': 'Asset Workflow Report',
      };
      doc.text(titleMap[reportType] || 'ProVisionXR Report', 105, currentY, null, null, 'center');

      currentY += 10;
      doc.setFontSize(14);
      doc.text(new Date().toLocaleString(), 105, currentY, 'center');

      // footer
      const footerY = doc.internal.pageSize.getHeight() - 30;
      doc.setFontSize(10);
      doc.text('Sign-Off Signature:', 15, footerY);
      doc.line(15, footerY + 3, doc.internal.pageSize.getWidth() - 15, footerY + 3);

      // 2) build table rows with correct min heights for images
      const bodyWithImageRows = resolved.map((step) => {
        const hasPhoto = typeof step.photo === 'string' && step.photo.startsWith('data:image');
        const minHeight = hasPhoto ? MAX_MEDIA_HEIGHT + 12 : 12;

        return [
          { content: step.detail || '', styles: { minCellHeight: minHeight } },
          { content: step.response || '', styles: { minCellHeight: minHeight } },
          { content: step.remarks || '', styles: { minCellHeight: minHeight } },
          {
            content: hasPhoto ? ' ' : step.videoFile ? 'Video Attached' : '',
            styles: { minCellHeight: minHeight },
          },
        ];
      });

      doc.autoTable({
        startY: currentY + 15,
        theme: 'grid',
        head: [['Question', 'Response', 'Remarks', 'Media']],
        body: bodyWithImageRows,
        columnStyles: { 3: { cellWidth: 50 } },

        // 3) draw images synchronously (we already have dataURLs)
        didDrawCell: function (data) {
          if (data.section !== 'body' || data.column.index !== 3) return;

          const step = resolved[data.row.index];
          if (!(typeof step?.photo === 'string' && step.photo.startsWith('data:image'))) return;

          const x = data.cell.x + 2;
          const y = data.cell.y + 2;
          const width = data.cell.width - 4;

          // assume landscape-ish; keep within MAX_MEDIA_HEIGHT
          let height = width / 1.5;
          if (height > MAX_MEDIA_HEIGHT) height = MAX_MEDIA_HEIGHT;

          try {
            doc.addImage(step.photo, 'PNG', x, y, width, height);
          } catch {
            // ignore image add failures but keep table
          }

          doc.setFontSize(6);
          doc.text(step.photoTimestamp || 'Captured: unknown time', x, y + height + 4);
        },
      });

      // 4) upload any videos and link them
      let videoLinksY = doc.lastAutoTable.finalY + 10;
      for (let i = 0; i < resolved.length; i++) {
        const step = resolved[i];
        if (step.videoFile) {
          const videoRef = ref(
            storage,
            `reports/videos/${reportType}_video_step${i + 1}_${Date.now()}.mp4`
          );
          await uploadBytes(videoRef, step.videoFile);
          const videoURL = await getDownloadURL(videoRef);
          doc.setFontSize(10);
          doc.textWithLink(`Step ${i + 1} Video: ${videoURL}`, 15, videoLinksY, { url: videoURL });
          videoLinksY += 10;
        }
      }

      // 5) upload PDF
      const blob = doc.output('blob');
      const filename = `ProVisionXR_${reportType}_Report_${Date.now()}.pdf`;
      const pdfRef = ref(storage, `reports/${filename}`);
      await uploadBytes(pdfRef, blob);
      const downloadURL = await getDownloadURL(pdfRef);

      if (!isMounted) return;

      setPdfUrl(downloadURL);
      setToastMsg('✅ Report & media saved to cloud');

      // give the toast time to be seen on Android
      setTimeout(() => setToastMsg(''), 1500);
      setTimeout(() => {
        if (isMounted && onComplete) onComplete(downloadURL);
      }, 1000);
    };

    generateAndUploadPDF();
    return () => {
      isMounted = false;
    };
  }, [formData, reportType, onComplete]);

  useEffect(() => {
    if (pdfUrl) setViewMode('view');
  }, [pdfUrl]);

  return (
    <div>
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#28a745',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            zIndex: 9999,
          }}
        >
          {toastMsg}
        </div>
      )}

      <button onClick={() => setViewMode('view')}>View PDF</button>
      <button onClick={() => setViewMode('download')}>Download PDF</button>

      {viewMode === 'view' && pdfUrl && (
        <iframe src={pdfUrl} width="100%" height="600px" title="PDF Preview" />
      )}
      {viewMode === 'download' && pdfUrl && (
        <a
          href={pdfUrl}
          download={`ProVisionXR_${reportType}_Report.pdf`}
          style={{ display: 'block', marginTop: '20px' }}
        >
          Download PDF
        </a>
      )}
    </div>
  );
};

export default GeneratePDF;
